import type { FC, PropsWithChildren } from 'react';
import { createContext, useState, useMemo, useContext, useTransition, useEffect, useCallback } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ThemeId } from '@/js/libs/ui/src/V2/Palette';
import { palette } from '@/js/libs/ui/src/V2/Palette';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage';

const prefersDarkQuery = '(prefers-color-scheme: dark)';
function checkPrefersDark() {
  return typeof window !== 'undefined' && window.matchMedia?.(prefersDarkQuery).matches;
}

type ThemeSetting = ThemeId | 'system';

type Ctx = {
  themeId: ThemeId;
  isSwitching: boolean;
  checkTheme: (id: ThemeSetting) => boolean;
  setTheme: (id: ThemeSetting) => void;
};

const ThemeControllerContext = createContext<Ctx | null>(null);

export const ThemeControllerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { loadString, saveString } = useLocalStorage();

  // TODO: extend logic with useIdentityUser once the backend is ready
  const savedTheme = useMemo(() => loadString(StorageKey.THEME_ID) as ThemeSetting | null, [loadString]);

  const initial = useMemo(() => {
    if (!savedTheme) return 'primary';
    if (savedTheme === 'system') return checkPrefersDark() ? 'primaryDark' : 'primary';
    return savedTheme;
  }, [savedTheme]);

  const [themeId, setThemeIdState] = useState<ThemeId>(initial);
  const [isSystem, setSystem] = useState<boolean>(savedTheme === 'system');
  const [isPending, startTransition] = useTransition();

  const setTheme = useCallbackRef((id: ThemeSetting) => {
    startTransition(() => {
      if (id === 'system') {
        setThemeIdState(checkPrefersDark() ? 'primaryDark' : 'primary');
        setSystem(true);
      } else {
        setThemeIdState(id);
        setSystem(false);
      }
      saveString(StorageKey.THEME_ID, id);
    });
  });

  // eslint-disable-next-line no-restricted-syntax
  const checkTheme = useCallback((id: ThemeSetting) => (id === 'system' ? isSystem : !isSystem && themeId === id), [themeId, isSystem]);

  useEffect(() => {
    if (!isSystem || typeof window === 'undefined' || !window.matchMedia) return () => {};

    const mql = window.matchMedia(prefersDarkQuery);
    const onChange = (e: MediaQueryListEvent) => {
      setThemeIdState(e.matches ? 'primaryDark' : 'primary');
    };

    const currentPrefersDark = mql.matches;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeIdState(currentPrefersDark ? 'primaryDark' : 'primary');

    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [isSystem]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeId);

    const bg = palette[themeId].colors.grey05_1 ?? palette.primary.colors.grey05_1;
    let meta = document.querySelector('meta#theme-color-runtime[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.id = 'theme-color-runtime';
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg);
  }, [themeId]);

  const value = useMemo(() => ({ themeId, isSwitching: isPending, checkTheme, setTheme }), [themeId, isPending, checkTheme, setTheme]);
  return <ThemeControllerContext.Provider value={value}>{children}</ThemeControllerContext.Provider>;
};

export const useThemeController = () => {
  const ctx = useContext(ThemeControllerContext);
  if (!ctx) throw new Error('useThemeController must be used within ThemeControllerProvider');
  return ctx;
};
